export const BID_ADJUSTMENT_SITE = Object.freeze({
  CHANNELS: 'SITE_SET_CHANNELS',
  MOMENTS: 'SITE_SET_MOMENTS'
})

export const BID_ADJUSTMENT_SITE_OPTIONS = Object.freeze([
  { label: '微信视频号', value: BID_ADJUSTMENT_SITE.CHANNELS },
  { label: '微信朋友圈', value: BID_ADJUSTMENT_SITE.MOMENTS }
])

export const BID_ADJUSTMENT_COEFFICIENT = Object.freeze({
  MIN: 1.2,
  MAX: 5,
  DECIMAL_PLACES: 2
})

export const BID_ADJUSTMENT_COEFFICIENT_TYPE = Object.freeze({
  SHALLOW: 'shallow',
  DEEP: 'deep'
})

const coefficientDefinitions = Object.freeze({
  [BID_ADJUSTMENT_COEFFICIENT_TYPE.SHALLOW]: {
    formKey: 'bid_adjustment_shallow_coefficient',
    packageKey: 'bid_coefficient',
    label: '浅层提价系数'
  },
  [BID_ADJUSTMENT_COEFFICIENT_TYPE.DEEP]: {
    formKey: 'bid_adjustment_deep_coefficient',
    packageKey: 'deep_bid_coefficient',
    label: '深层提价系数'
  }
})

const coefficientTypes = Object.values(BID_ADJUSTMENT_COEFFICIENT_TYPE)
const supportedSites = BID_ADJUSTMENT_SITE_OPTIONS.map((item) => item.value)
const coefficientPattern = new RegExp(`^[0-9]+([.]{1}[0-9]{1,${BID_ADJUSTMENT_COEFFICIENT.DECIMAL_PLACES}})?$`)

export function createBidAdjustmentForm() {
  return {
    bid_adjustment_enabled: false,
    bid_adjustment_site_set: [],
    bid_adjustment_shallow_coefficient: '',
    bid_adjustment_deep_coefficient: '',
    bid_adjustment_original_enabled: false,
    bid_adjustment_clear_on_disable: false,
    bid_adjustment_unknown_sites: [],
    bid_adjustment_coefficients_reentry_required: false
  }
}

function getCoefficientKey(value) {
  if (value === '' || value === null || typeof value === 'undefined') return '__EMPTY__'
  const coefficient = Number(value)
  return Number.isNaN(coefficient) ? String(value) : String(coefficient)
}

function getCoefficientDefinition(type) {
  return coefficientDefinitions[type]
}

function getCoefficientKeys(packages, type) {
  const { packageKey } = getCoefficientDefinition(type)
  return new Set(packages.map((item) => getCoefficientKey(item?.[packageKey])))
}

function requiresCoefficientsReentry(packages) {
  if (packages.length < 2) return false

  return coefficientTypes.some((type) => {
    const keys = getCoefficientKeys(packages, type)
    return keys.size > 1 || keys.has('__EMPTY__')
  })
}

/**
 * 将媒体的 bid_adjustment 参数转换为预算表单状态。
 */
export function rebuildBidAdjustmentForm(bidAdjustment, { clearOnDisable = false } = {}) {
  const form = createBidAdjustmentForm()
  form.bid_adjustment_clear_on_disable = clearOnDisable

  const packages = Array.isArray(bidAdjustment?.site_set_package) ? bidAdjustment.site_set_package : []
  if (!packages.length) return form

  const siteSet = [...new Set(packages.flatMap((item) => (Array.isArray(item?.site_set) ? item.site_set : [])))]
  const coefficientsReentryRequired = requiresCoefficientsReentry(packages)

  form.bid_adjustment_enabled = true
  form.bid_adjustment_original_enabled = true
  form.bid_adjustment_site_set = siteSet.filter((site) => supportedSites.includes(site))
  form.bid_adjustment_unknown_sites = siteSet.filter((site) => !supportedSites.includes(site))
  form.bid_adjustment_coefficients_reentry_required = coefficientsReentryRequired
  coefficientTypes.forEach((type) => {
    const { formKey, packageKey } = getCoefficientDefinition(type)
    form[formKey] = coefficientsReentryRequired ? '' : packages[0]?.[packageKey] ?? ''
  })

  return form
}

export function validateBidAdjustmentSiteSet(form) {
  if (!form.bid_adjustment_enabled) return ''
  if (form.bid_adjustment_unknown_sites?.length) {
    return `原计划包含不支持的提价版位：${form.bid_adjustment_unknown_sites.join('、')}，请重新选择`
  }
  if (!form.bid_adjustment_site_set?.length) return '请至少选择一个提价版位'
  return ''
}

export function validateBidAdjustmentCoefficient(form, type) {
  if (!form.bid_adjustment_enabled) return ''
  if (form.bid_adjustment_coefficients_reentry_required) return '原计划存在不一致或缺失的浅层或深层提价系数，请重新输入'

  const { formKey, label } = getCoefficientDefinition(type)
  const value = form[formKey]
  if (value === '' || value === null || typeof value === 'undefined') return `请输入${label}`
  if (!coefficientPattern.test(String(value))) {
    return `${label}最多保留${BID_ADJUSTMENT_COEFFICIENT.DECIMAL_PLACES}位小数`
  }

  const coefficient = Number(value)
  if (coefficient < BID_ADJUSTMENT_COEFFICIENT.MIN || coefficient > BID_ADJUSTMENT_COEFFICIENT.MAX) {
    return `${label}范围为${BID_ADJUSTMENT_COEFFICIENT.MIN} ~ ${BID_ADJUSTMENT_COEFFICIENT.MAX}`
  }
  return ''
}

/**
 * 根据表单和页面上下文构造媒体参数。
 * 修改入口关闭原有配置时传空对象；新建、复制和非智投场景省略字段。
 */
export function makeBidAdjustmentParams(form, { isSmartDelivery = false, taskId } = {}) {
  if (!isSmartDelivery) return {}

  if (form.bid_adjustment_enabled) {
    const error =
      validateBidAdjustmentSiteSet(form) ||
      validateBidAdjustmentCoefficient(form, BID_ADJUSTMENT_COEFFICIENT_TYPE.SHALLOW) ||
      validateBidAdjustmentCoefficient(form, BID_ADJUSTMENT_COEFFICIENT_TYPE.DEEP)
    if (error) throw new Error(`${taskId ? `任务 ${taskId}：` : ''}${error}`)

    const { formKey: shallowCoefficientFormKey, packageKey: shallowCoefficientPackageKey } = getCoefficientDefinition(
      BID_ADJUSTMENT_COEFFICIENT_TYPE.SHALLOW
    )
    const { formKey: deepCoefficientFormKey, packageKey: deepCoefficientPackageKey } = getCoefficientDefinition(
      BID_ADJUSTMENT_COEFFICIENT_TYPE.DEEP
    )

    return {
      bid_adjustment: {
        site_set_package: [
          {
            site_set: [...form.bid_adjustment_site_set],
            [shallowCoefficientPackageKey]: Number(form[shallowCoefficientFormKey]),
            [deepCoefficientPackageKey]: Number(form[deepCoefficientFormKey])
          }
        ]
      }
    }
  }

  if (form.bid_adjustment_original_enabled && form.bid_adjustment_clear_on_disable) {
    return { bid_adjustment: {} }
  }

  return {}
}
